import React, { useState } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import {
  IconButton,
  Popper,
  ClickAwayListener,
  Box,
  Stack,
  Button,
  Typography,
  useTheme,
} from '@mui/material';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';

interface Reaction {
  emoji: string;
  count: number;
  reactedByMe?: boolean;
}

interface ReactionsProps {
  onSelect?: (emoji: string) => void;
  reactionData?: Reaction[];
  readOnly?: boolean;
}

const Reactions: React.FC<ReactionsProps> = ({ onSelect, reactionData = [], readOnly = false }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEmojiSelect = (emoji: any) => {
    const emojiNative = emoji.native;
    onSelect?.(emojiNative);
    handleClose();
  };

  const handleReactionClick = (reaction: Reaction) => {
    onSelect?.(reaction.emoji);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            maxWidth: 400,
            rowGap: 0.5,
            mt: 0.2,
          }}
        >
          {reactionData.map((reaction) => (
            <Button
              key={reaction.emoji}
              variant={reaction.reactedByMe ? 'contained' : 'outlined'}
              onClick={() => {
                handleReactionClick(reaction);
              }}
              sx={{
                borderRadius: '999px',
                minWidth: 'auto',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                borderColor: theme.palette.grey[400],
                backgroundColor: reaction.reactedByMe ? theme.palette.grey[300] : undefined,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.grey[600],
                  backgroundColor: theme.palette.grey[100],
                },
              }}
            >
              <Typography fontSize={13}>{reaction.emoji}</Typography>
              <Typography fontSize={10}>{reaction.count}</Typography>
            </Button>
          ))}
          {!readOnly && (
            <IconButton onClick={handleClick} size="small">
              <EmojiEmotionsIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Popper open={open} anchorEl={anchorEl} placement="top-start" sx={{ zIndex: 3000 }}>
          <Box sx={{ zIndex: 3000, boxShadow: 3 }}>
            <Picker data={data} onEmojiSelect={handleEmojiSelect} theme="light" />
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export { Reactions };
